import React, { useId } from "react";
import { Area, AreaChart, ResponsiveContainer, YAxis } from "recharts";

// Brand palette (matches SeriesChart): violet #9747FF, Zenit pink #EC4899.
// Light fill + a soft glow on the stroke so the trend reads as a clean glowing
// line rather than a heavy filled area.
const TONES = {
  violet: { stroke: "rgba(151,71,255,1)", fill: "rgba(151,71,255,0.16)", glow: "rgba(151,71,255,0.55)" },
  pink: { stroke: "rgba(236,72,153,1)", fill: "rgba(236,72,153,0.15)", glow: "rgba(236,72,153,0.5)" },
};

/**
 * Tiny trend line for a stat card. `values` is a plain number[] (real series
 * from workflowPerformance). Renders nothing extra when the series is flat/empty
 * so cards without a trend still look intentional.
 */
export function Sparkline({ values = [], tone = "violet", height = 44, className = "" }) {
  const gradientId = useId();
  const series = (Array.isArray(values) ? values : [])
    .map((v) => Number(v))
    .filter((v) => Number.isFinite(v));

  if (series.length < 2) {
    return <div style={{ height }} className={className} aria-hidden />;
  }

  const data = series.map((value, index) => ({ index, value }));
  const t = TONES[tone] || TONES.violet;
  const min = Math.min(...series);
  const max = Math.max(...series);
  // Small headroom so the line never touches the top/bottom edge.
  const pad = (max - min || 1) * 0.15;

  return (
    <div style={{ height }} className={className} aria-hidden>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={t.fill} />
              <stop offset="100%" stopColor="rgba(0,0,0,0)" />
            </linearGradient>
          </defs>
          <YAxis hide domain={[min - pad, max + pad]} />
          <Area
            type="monotone"
            dataKey="value"
            stroke={t.stroke}
            strokeWidth={2.5}
            fill={`url(#${gradientId})`}
            isAnimationActive={false}
            dot={false}
            style={{ filter: `drop-shadow(0 2px 6px ${t.glow})` }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export default Sparkline;
